import produce from 'immer';
import {
  AmDestinations,
  Location,
  LocationSuggestions,
  LocationType,
  RouteCffData,
  TravelClass,
  TravelType,
  TrendingDestinations,
  WidgetTab,
} from '../../common';
import {
  cancelGetLocationByLatLong,
  cancelGetLocations,
  cancelLocationSearch,
  getAMAvailability,
  getAMDestinations,
  getAMOrigins,
  getLocationByLatLong,
  getLocations,
  getRouteCffs,
  getSuggestedLocations,
  getTrendingDestinations,
  locationSearch,
  Response,
  Service,
  Status,
} from '../../services';
import { isIn, isNotIn } from '../../utils';
import { changed, changedFactory, debounce, isNotBlank, isNotPresent, isPresent } from '../../utils/';
import { AmAvailability, BookingState, CompactWidgetState, DEFAULT_TRAVEL_CLASSES } from '../state';
import { isLoading, locationsChanged, locationsSet, originChanged } from '../stateUtils';
import { LifecycleEvent, SideEffect, SideEffectGenerator, Store } from '../types';
import { bindActions } from 'redux-zero/utils';

const debouncedLocationSearch = debounce(locationSearch, 200); // Debounce 200 milliseconds

// == Utilities ================================================================

const parseTravelClasses = (routeCffs: RouteCffData): TravelClass[] => {
  const travelClasses = [];
  if (routeCffs.cffMixed) {
    travelClasses.push(TravelClass.MIXED);
  }
  if (routeCffs.cffEconomy) {
    travelClasses.push(TravelClass.ECONOMY);
  }
  if (routeCffs.cffPremiumEconomy) {
    travelClasses.push(TravelClass.PREMIUM_ECONOMY);
  }
  if (routeCffs.cffBusiness) {
    travelClasses.push(TravelClass.BUSINESS);
  }
  return travelClasses;
};

// == Side Effects =============================================================

export const getSuggestedLocationsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getSuggestedLocationsStart: (state) =>
        produce(state, ({ ux, location }: BookingState) => {
          ux.loading.getSuggestedLocations = true;
          delete location.suggestions;
        }),
      _getSuggestedLocationsFinish: (state, { status, payload }: Response<LocationSuggestions>) =>
        produce(state, ({ ux, location }: BookingState) => {
          ux.loading.getSuggestedLocations = false;
          if (status === Status.OK) {
            location.suggestions = payload;
          }
        }),
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        selection: { locale },
      } = readOnlyState;

      effectActions._getSuggestedLocationsStart();
      getSuggestedLocations(envConfig, locale).then(({ status, payload }: Response<LocationSuggestions>) => {
        effectActions._getSuggestedLocationsFinish({ status, payload });
      });
    },
    when: changedFactory('selection', 'locale'),
    runOn: [{ type: LifecycleEvent.INIT }],
    name: getSuggestedLocationsEffect.name,
  };
};

export const getTrendingDestinationsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getTrendingDestinationsStart: (state) =>
        produce(state, ({ ux, location }: BookingState) => {
          ux.loading.getTrendingLocations = true;
          delete location.trending;
        }),
      _getTrendingDestinationsFinish: (state, { status, payload }: Response<TrendingDestinations>) =>
        produce(state, ({ ux, location }: BookingState) => {
          ux.loading.getTrendingLocations = false;
          if (status === Status.OK) {
            location.trending = payload;
          }
        }),
    },
    store
  );

  const when = (prev: BookingState, next: BookingState) => {
    const { origin } = next.selection.locations[next.selection.activeLocationIndex];
    return origin?.locationCode && (originChanged(prev, next) || changed(prev, next, 'selection', 'locale'));
  };

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        selection: { locale, locations, activeLocationIndex },
      } = readOnlyState;
      effectActions._getTrendingDestinationsStart();
      getTrendingDestinations(envConfig, locale, locations[activeLocationIndex].origin.locationCode).then(
        ({ status, payload }: Response<TrendingDestinations>) => {
          effectActions._getTrendingDestinationsFinish({ status, payload });
        }
      );
    },
    when,
    runOn: [
      {
        type: LifecycleEvent.INIT,
        when,
      },
    ],
    name: getSuggestedLocationsEffect.name,
  };
};

export const getLocationsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getLocationsStart: (state) =>
        produce(state, ({ ux }: BookingState) => {
          ux.loading.getLocations = true;
        }),
      _getLocationsFinish: (state, locationSearches: Response<Location>[]) =>
        produce(state, ({ ux, selection }: BookingState) => {
          const findLocationMatch = (locationCode) => {
            return locationSearches.find((search) => {
              return search.status === Status.OK && search.payload.locationCode === locationCode;
            })?.payload;
          };
          ux.loading.getLocations = false;

          selection.locations.forEach((ond) => {
            if (ond.origin) {
              ond.origin = findLocationMatch(ond.origin.locationCode) || ond.origin;
            }

            if (ond.destination) {
              ond.destination = findLocationMatch(ond.destination.locationCode) || ond.destination;
            }
          });

          if (
            selection?.locations[selection.activeLocationIndex]?.origin &&
            selection?.locations[selection.activeLocationIndex]?.destination
          ) {
            ux.compactWidgetState = CompactWidgetState.OPENED;
          }
        }),
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        selection: { locations, locale },
      } = readOnlyState;

      effectActions._getLocationsStart();
      getLocations(envConfig, locations, locale).then((locationSearches: Response<Location>[]) => {
        effectActions._getLocationsFinish(locationSearches);
      });
    },
    when: (prev, next) => {
      const { origin, destination } = next.selection.locations[next.selection.activeLocationIndex];
      if (!origin?.locationCode && !destination?.locationCode) {
        return false;
      }
      if (!locationsChanged(prev, next) && isLoading(next, Service.GET_LOCATIONS)) {
        return false;
      }
      return (
        changed(prev, next, 'selection', 'locale') ||
        (isPresent(origin) && isNotPresent(origin.cityName)) ||
        (isPresent(destination) && isNotPresent(destination.cityName)) ||
        prev.selection.locations[prev.selection.activeLocationIndex]?.origin?.locationCode !== origin?.locationCode ||
        prev.selection.locations[prev.selection.activeLocationIndex]?.destination?.locationCode !==
          destination?.locationCode
      );
    },
    name: getLocationsEffect.name,
  };
};

export const cancelGetLocationsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _cancelGetLocations: (state) => {
        cancelGetLocations();
        return state;
      },
    },
    store
  );

  return {
    runSideEffects: effectActions._cancelGetLocations,
    // when: (prev, next) => locationsChanged(prev, next) && isLoading(next, Service.GET_LOCATIONS),
    // FIXME: Temporaily disable this effect (run only on init) since there seems to be some race conditions / falsy logic
    // happening occasionally with location updates
    when: () => false,
    name: cancelGetLocationsEffect.name,
  };
};

export const clearNonMatchingContinentCode = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      clearNonMatchingLocation: (state, locationsIndex, locationType) =>
        produce(state, ({ selection }: BookingState) => {
          selection.locations[locationsIndex][locationType] = undefined;
        }),
    },
    store
  );

  return {
    runSideEffects: ({ selection }) => {
      const returnOrigin = selection.locations[1].origin;
      const returnDestination = selection.locations[1].destination;

      if (
        returnDestination?.locationCode &&
        selection.locations[0]?.origin?.locationCode &&
        selection.locations[0]?.origin?.continentCode !== returnDestination?.continentCode
      ) {
        effectActions.clearNonMatchingLocation(1, LocationType.DESTINATION);
      }

      if (
        returnOrigin?.locationCode &&
        selection.locations[0]?.destination?.locationCode &&
        selection.locations[0]?.destination?.continentCode !== returnOrigin.continentCode
      ) {
        effectActions.clearNonMatchingLocation(1, LocationType.ORIGIN);
      }
    },
    when: (prev, next) => locationsChanged(prev, next) && next.selection.travelType === TravelType.MULTI_CITY,
    name: clearNonMatchingContinentCode.name,
  };
};

export const locationSearchEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _locationSearchStart: (state) =>
        produce(state, ({ ux }: BookingState) => {
          ux.loading.locationSearch = true;
        }),
      _locationSearchFinish: (state, { status, payload }: Response<Location[]>) =>
        produce(state, ({ ux, location }: BookingState) => {
          ux.loading.locationSearch = false;
          if (status === Status.OK) {
            location.results = payload;
            ux.activeLocationIndex = 0;
          }
        }),
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const { envConfig, selection, location, ux } = readOnlyState;
      const oppositeLocationType = {
        [LocationType.ORIGIN]: LocationType.DESTINATION,
        [LocationType.DESTINATION]: LocationType.ORIGIN,
      }[ux.locationType];

      let continentCode = null;

      if (selection.travelType === TravelType.MULTI_CITY && selection.activeLocationIndex === 1) {
        continentCode = selection.locations[0][oppositeLocationType].continentCode;
      }

      effectActions._locationSearchStart();
      debouncedLocationSearch(envConfig, location?.query, selection?.locale, continentCode).then(
        ({ status, payload }: Response<Location[]>) => {
          effectActions._locationSearchFinish({ status, payload });
        }
      );
    },
    when: (prev, next) => changed(prev, next, 'location', 'query') && isNotBlank(next.location.query),
    name: locationSearchEffect.name,
  };
};

export const clearSecondLocationsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _clearSecondLocation: (state) =>
        produce(state, ({ selection }: BookingState) => {
          selection.locations = [selection.locations[0]];
        }),
    },
    store
  );

  return {
    runSideEffects: () => {
      effectActions._clearSecondLocation();
    },
    when: (_prev, next) => {
      return next.selection.travelType !== TravelType.MULTI_CITY && next.selection.locations.length > 1;
    },
    name: clearSecondLocationsEffect.name,
  };
};

export const cancelLocationQueryEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _cancelLocationQuery: (state) => {
        cancelLocationSearch();
        return state;
      },
    },
    store
  );

  return {
    runSideEffects: effectActions._cancelLocationQuery,
    when: (prev, next) => locationsChanged(prev, next) && isLoading(next, Service.LOCATION_SEARCH),
    name: cancelLocationQueryEffect.name,
  };
};

export const getRouteCffsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getRouteCffsStart: (state) =>
        produce(state, ({ location, ux }: BookingState) => {
          ux.loading.getRouteCffs = true;
          location.fppEnabled = false; // Defaulting to false during the backend call
        }),
      _getRouteCffsFinish: (state, { status, payload }: Response<RouteCffData>) =>
        produce(state, ({ ux, location, selection }: BookingState) => {
          ux.loading.getRouteCffs = false;
          if (isIn(status, [Status.OK, Status.ERROR])) {
            // Display route-not-supported notification when customer facing routeCff failure in award tab
            if (status === Status.ERROR && selection.activatedTab === WidgetTab.AWARD) {
              ux.warnings.routeNotSupported = true;
              selection.locations[selection.activeLocationIndex].origin = undefined;
              selection.locations[selection.activeLocationIndex].destination = undefined;
            } else {
              ux.warnings.routeNotSupported = false;
            }

            if (status === Status.OK) {
              location.fppEnabled = payload.fppEnabled;
            }
            const oldTravelClasses: TravelClass[] = ux.travelClasses;
            ux.travelClasses = status === Status.OK ? parseTravelClasses(payload) : DEFAULT_TRAVEL_CLASSES;
            // MIXED class needs to be automatically selected if it appears (only supported with ADULT)
            if (
              isNotIn(TravelClass.MIXED, oldTravelClasses) &&
              isIn(TravelClass.MIXED, ux.travelClasses) &&
              isNotIn(selection.travelClass, ux.travelClasses)
            ) {
              selection.travelClass = TravelClass.MIXED;
            } else if (
              selection.travelClass === TravelClass.ECONOMY &&
              isNotIn(TravelClass.MIXED, oldTravelClasses) &&
              isIn(TravelClass.MIXED, ux.travelClasses)
            ) {
              selection.travelClass = TravelClass.MIXED;
            } else if (isNotIn(selection.travelClass, ux.travelClasses)) {
              // If selected type is not available on the route, fall back to ECONOMY
              selection.travelClass = TravelClass.ECONOMY;
            }
          }
        }),
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        selection: { locations, activeLocationIndex, locale, isCountryShopTestGroup },
        currentTab,
      } = readOnlyState;

      effectActions._getRouteCffsStart();

      getRouteCffs(
        envConfig,
        locations[activeLocationIndex].origin,
        locations[activeLocationIndex].destination,
        currentTab === WidgetTab.AWARD,
        locale,
        isCountryShopTestGroup
      ).then(({ status, payload }: Response<RouteCffData>) => {
        effectActions._getRouteCffsFinish({ status, payload });
      });
    },
    when: (prev, next) =>
      (locationsChanged(prev, next) && locationsSet(next.selection)) ||
      (changed(prev, next, 'currentTab') && locationsSet(next.selection)),
    runOn: [
      {
        type: LifecycleEvent.INIT,
        when: (_prev, next) => locationsSet(next.selection),
      },
    ],
    name: getRouteCffsEffect.name,
  };
};

export const getUserLocationEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getUserLocationStart: (state) =>
        produce(state, ({ ux }: BookingState) => {
          ux.loading.getLocationByLatLong = true;
        }),
      _getUserLocationFinish: (state, { status, payload }: Response<Location>) =>
        produce(state, ({ ux, selection }: BookingState) => {
          ux.loading.getLocationByLatLong = false;
          ux.locatingUser = false;
          if (status === Status.OK) {
            if (
              selection?.locations[selection.activeLocationIndex]?.destination?.locationCode === payload.locationCode
            ) {
              selection.locations[selection.activeLocationIndex].destination = undefined;
            }
            selection.locations[selection.activeLocationIndex].origin = payload;
          }
        }),
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const { envConfig, ux, selection } = readOnlyState;

      effectActions._getUserLocationStart();
      getLocationByLatLong(envConfig, ux.userLocation, selection.locale).then(
        ({ status, payload }: Response<Location>) => {
          effectActions._getUserLocationFinish({ status, payload });
        }
      );
    },
    when: (prev, next): boolean =>
      changed(prev, next, 'ux', 'userLocation') &&
      isNotPresent(next.selection.locations[next.selection.activeLocationIndex].origin),
    name: getUserLocationEffect.name,
  };
};

export const cancelGetUserLocationEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _cancelGetUserLocation: (state) => {
        cancelGetLocationByLatLong();
        return state;
      },
    },
    store
  );

  return {
    runSideEffects: effectActions._cancelGetUserLocation,
    when: (prev, next): boolean => locationsChanged(prev, next) && isLoading(next, Service.GET_LOCATION_BY_LAT_LONG),
    name: cancelGetUserLocationEffect.name,
  };
};

export const getAMDestinationsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getAMDestinationsEffectFinnish: (state, { destinations, holidayTypes }: AmDestinations) => {
        return produce(state, (currentState: BookingState) => {
          currentState.amState.destinations = destinations;
          currentState.amState.holidayTypes = holidayTypes;
          if (
            !currentState.amState.destinations.some(
              (dest) => currentState.amState.amSelection.locations.destination?.destinationCode === dest.destinationCode
            )
          ) {
            delete currentState.amState.amSelection.locations.destination;
            delete currentState.amState.amSelection.locations.holidayType;
          }
        });
      },
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        amState: {
          amSelection: { locations },
        },
      } = readOnlyState;

      getAMDestinations(envConfig, locations?.origin?.locationCode || '').then(
        ({ destinations, holidayTypes }: AmDestinations) =>
          effectActions._getAMDestinationsEffectFinnish({ destinations, holidayTypes })
      );
    },
    when: (prev, next): boolean =>
      changed(prev, next, 'amState', 'amSelection', 'locations', 'origin') ||
      (prev.currentTab !== WidgetTab.AM &&
        next.currentTab === WidgetTab.AM &&
        !!prev.amState.amSelection.locations?.origin),
    name: getAMDestinationsEffect.name,
  };
};

export const getAMOriginsEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getAMOriginsEffectFinnish: (state, { origins }: { origins: Location[] }) => {
        return produce(state, ({ amState }: BookingState) => {
          amState.origins = origins;
          if (!isPresent(amState.amSelection?.locations?.origin)) {
            amState.amSelection.locations.origin = origins[0];
          }
        });
      },
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const { envConfig } = readOnlyState;

      getAMOrigins(envConfig).then((origins) => effectActions._getAMOriginsEffectFinnish({ origins }));
    },
    when: (prev, next): boolean => prev.currentTab !== WidgetTab.AM && next.currentTab === WidgetTab.AM,
    name: getAMOriginsEffect.name,
  };
};

export const getAMAvailabilityEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getAMAvailabilityEffectFinnish: (state, availability: AmAvailability) => {
        return produce(state, ({ amState }: BookingState) => {
          amState.availability = availability;
        });
      },
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        amState: {
          amSelection: { locations, rooms },
        },
      } = readOnlyState;

      getAMAvailability(
        envConfig,
        locations?.origin?.locationCode,
        locations?.destination?.destinationCode,
        rooms
      ).then((availability: AmAvailability) => effectActions._getAMAvailabilityEffectFinnish(availability));
    },
    when: (prev, next): boolean =>
      ((prev.currentTab !== WidgetTab.AM &&
        next.currentTab === WidgetTab.AM &&
        !!prev.amState.amSelection.locations?.origin &&
        !!prev.amState.amSelection.locations?.destination) ||
        changed(prev, next, 'amState', 'amSelection', 'locations', 'origin') ||
        changed(prev, next, 'amState', 'amSelection', 'locations', 'destination') ||
        changed(prev, next, 'amState', 'amSelection', 'rooms')) &&
      !!next.amState.amSelection.locations?.origin?.locationCode &&
      !!next.amState.amSelection.locations?.destination?.destinationCode,
    name: getAMAvailabilityEffect.name,
  };
};

export const locationsEffects: SideEffectGenerator<BookingState>[] = [
  getSuggestedLocationsEffect,
  getTrendingDestinationsEffect,
  cancelGetLocationsEffect,
  getLocationsEffect,
  cancelLocationQueryEffect,
  locationSearchEffect,
  getRouteCffsEffect,
  getUserLocationEffect,
  cancelGetUserLocationEffect,
  getAMDestinationsEffect,
  getAMOriginsEffect,
  getAMAvailabilityEffect,
  clearNonMatchingContinentCode,
  clearSecondLocationsEffect,
];
